import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { History, RotateCcw, Clock } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { pl, enUS } from "date-fns/locale";
import { ListSkeleton } from "@/components/skeletons";

interface OfferSheetHistoryEntry {
  id: string;
  offerSheetId: string;
  userId: string;
  title: string;
  content: string;
  version: number;
  changeType: "create" | "update" | "delete";
  changeReason: string | null;
  createdAt: string;
}

interface OfferSheetHistoryProps {
  offerSheetId: string;
  offerSheetTitle: string;
}

export default function OfferSheetHistory({
  offerSheetId,
  offerSheetTitle,
}: OfferSheetHistoryProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "pl" ? pl : enUS;
  const [open, setOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<OfferSheetHistoryEntry | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch history for the offer sheet
  const { data: history = [], isLoading } = useQuery<OfferSheetHistoryEntry[]>({
    queryKey: [`/api/offer-sheets/${offerSheetId}/history`],
    enabled: open,
  });

  // Restore from history mutation
  const restoreMutation = useMutation({
    mutationFn: async (historyId: string) => {
      // apiRequest oddaje juz sparsowane cialo — `.json()` na zwyklym obiekcie
      // rzucalo TypeError. Bug byl niewidoczny, dopoki sygnatura zwracala `any` (VP-326).
      return apiRequest<{ message: string; offerSheet: unknown }>(
        "POST",
        `/api/offer-sheets/${offerSheetId}/restore/${historyId}`
      );
    },
    onSuccess: (data) => {
      setOpen(false);
      toast({
        title: t("offerSheetHistory.toast.restored"),
        description: t("offerSheetHistory.toast.restoredDesc"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/offer-sheets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/offer-sheets/active"] });
    },
    onError: (error) => {
      toast({
        title: t("offerSheetHistory.toast.retryTitle"),
        description: t("offerSheetHistory.toast.retryDesc"),
        variant: "destructive",
      });
    },
  });

  const handleRestore = (historyEntry: OfferSheetHistoryEntry) => {
    const formattedDate = format(new Date(historyEntry.createdAt), "dd.MM.yyyy HH:mm", {
      locale: dateLocale,
    });
    if (
      confirm(
        t("offerSheetHistory.confirmRestore", { title: historyEntry.title, date: formattedDate })
      )
    ) {
      restoreMutation.mutate(historyEntry.id);
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    return t(`offerSheetHistory.changeType.${changeType}`, { defaultValue: changeType });
  };

  const getChangeTypeBadgeVariant = (changeType: string) => {
    switch (changeType) {
      case "create":
        return "default";
      case "update":
        return "secondary";
      case "delete":
        return "destructive";
      default:
        return "default";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="button-show-history">
          <History className="h-4 w-4 mr-2" />
          {t("offerSheetHistory.button")}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-4xl max-h-[80vh] overflow-hidden"
        data-testid="dialog-offer-history"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            {t("offerSheetHistory.dialogTitle", { title: offerSheetTitle })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            // VP-208: placeholder listy historii zamiast spinnera.
            // data-testid zachowany — używa go test E2E.
            <div data-testid="loading-history">
              <ListSkeleton rows={4} withAvatar={false} />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-history">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("offerSheetHistory.empty")}</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {history.map((entry) => (
                <Card key={entry.id} className="border" data-testid={`history-entry-${entry.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={getChangeTypeBadgeVariant(entry.changeType)}>
                          {getChangeTypeLabel(entry.changeType)}
                        </Badge>
                        <span className="text-sm font-medium">{entry.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(entry.createdAt), {
                            addSuffix: true,
                            locale: dateLocale,
                          })}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestore(entry)}
                          disabled={restoreMutation.isPending}
                          data-testid={`button-restore-${entry.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          {t("offerSheetHistory.restore")}
                        </Button>
                      </div>
                    </div>
                    {entry.changeReason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("offerSheetHistory.reason")}: {entry.changeReason}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="bg-muted/50 rounded-md p-3 max-h-32 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap text-muted-foreground">
                        {entry.content.substring(0, 300)}
                        {entry.content.length > 300 && "..."}
                      </pre>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {format(new Date(entry.createdAt), "dd.MM.yyyy, HH:mm:ss", {
                        locale: dateLocale,
                      })}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
